import { useState, useEffect } from 'react';
import { SEO } from '../../../utils/seo';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import RetainerSavingsChart from './components/RetainerSavingsChart';

interface CalculatorInputs {
  acreage: number;
  serviceType: string;
  complexity: string;
  priorityTurnaround: boolean;
  executiveSummary: boolean;
}

const TimberForestryPage = () => {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    acreage: 250,
    serviceType: 'timber-inventory',
    complexity: 'standard',
    priorityTurnaround: false,
    executiveSummary: false,
  });

  const [priceRange, setPriceRange] = useState({ low: 0, high: 0 });
  const [adjustedRate, setAdjustedRate] = useState({ low: 0, high: 0 });

  const serviceTypes = [
    { value: 'timber-inventory', label: 'Timber Inventory (RGB)', baseLow: 6, baseHigh: 12 },
    { value: 'forest-health', label: 'Forest Health (Multispectral)', baseLow: 12, baseHigh: 25 },
    { value: 'lidar-survey', label: 'LiDAR Forestry Survey', baseLow: 30, baseHigh: 75 },
    { value: 'pre-acquisition', label: 'Pre-Acquisition Valuation', baseLow: 15, baseHigh: 30 },
    { value: 'post-storm', label: 'Post-Storm Assessment', baseLow: 10, baseHigh: 25 },
  ];

  const getServiceData = () => {
    return serviceTypes.find(s => s.value === inputs.serviceType) || serviceTypes[0];
  };

  const calculatePrice = () => {
    const service = getServiceData();
    let { baseLow, baseHigh } = service;

    // Small-acreage scaling
    let multiplier = 1.0;
    if (inputs.acreage < 250) {
      const acres = Math.max(inputs.acreage, 10);
      multiplier = 1.6 - ((acres - 10) * (0.6 / 240));
    }

    // Apply complexity
    if (inputs.complexity === 'complex') {
      baseLow *= 1.2;
      baseHigh *= 1.2;
    }

    // Calculate adjusted per-acre rate
    const adjLow = baseLow * multiplier;
    const adjHigh = baseHigh * multiplier;
    setAdjustedRate({ low: adjLow, high: adjHigh });

    // Calculate total
    let totalLow = adjLow * inputs.acreage;
    let totalHigh = adjHigh * inputs.acreage;

    // Apply add-ons
    if (inputs.priorityTurnaround) {
      totalLow *= 1.15;
      totalHigh *= 1.15;
    }
    if (inputs.executiveSummary) {
      totalLow *= 1.2;
      totalHigh *= 1.2;
    }

    setPriceRange({ low: Math.round(totalLow), high: Math.round(totalHigh) });
  };

  useEffect(() => {
    calculatePrice();
  }, [inputs]);

  const services = [
    {
      title: 'Timber Inventory & Stand Mapping (RGB)',
      description: 'High-resolution mapping to define stand boundaries, density, and access.',
      why: 'Poor inventory data leads to mispriced timber and weak negotiating leverage.',
      image: 'https://public.readdy.ai/ai/img_res/d78d9fa3582880b2238417f6e5acccf3.jpg',
    },
    {
      title: 'Forest Health & Stress Analysis (Multispectral)',
      description: 'Vegetation health analysis to identify disease, pest pressure, drought stress, and die-off.',
      why: 'Stress shows up before loss, but only if you\'re looking.',
      image: 'https://readdy.ai/api/search-image?query=forest%20aerial%20imagery%20with%20vegetation%20health%20analysis%20overlay%20showing%20color%20coded%20stress%20indicators%20across%20tree%20canopy%20multispectral%20data%20visualization%20with%20healthy%20green%20and%20stressed%20yellow%20red%20areas%20scientific%20forestry%20mapping%20style&width=800&height=600&seq=timber2&orientation=landscape',
    },
    {
      title: 'LiDAR Forestry Surveys',
      description: 'LiDAR-based canopy, terrain, and structure analysis for volume, biomass, and terrain modeling.',
      why: 'LiDAR removes guesswork from volume estimates and harvest planning.',
      image: 'https://readdy.ai/api/search-image?query=forest%20terrain%20with%20lidar%20point%20cloud%20visualization%20showing%20three%20dimensional%20tree%20structure%20and%20ground%20elevation%20data%20technical%20forestry%20survey%20with%20detailed%20canopy%20height%20model%20and%20topographic%20features%20professional%20geospatial%20analysis%20style&width=800&height=600&seq=timber3&orientation=landscape',
    },
    {
      title: 'Pre-Acquisition & Valuation Surveys',
      description: 'Independent timber and land intelligence for buyers, sellers, and funds.',
      why: 'Bad assumptions at acquisition compound for decades.',
      image: 'https://readdy.ai/api/search-image?query=forested%20land%20parcel%20overview%20from%20high%20altitude%20showing%20property%20boundary%20lines%20and%20timber%20stand%20divisions%20with%20clear%20access%20roads%20and%20natural%20features%20professional%20real%20estate%20and%20investment%20documentation%20style&width=800&height=600&seq=timber4&orientation=landscape',
    },
    {
      title: 'Post-Storm & Insurance Assessments',
      description: 'Rapid damage assessment after storms, fire, or flooding.',
      why: 'Documentation determines recovery speed and claim outcomes.',
      image: 'https://readdy.ai/api/search-image?query=storm%20damaged%20forest%20aerial%20view%20showing%20fallen%20trees%20and%20broken%20canopy%20areas%20with%20visible%20destruction%20patterns%20and%20intact%20surrounding%20forest%20for%20comparison%20professional%20disaster%20assessment%20documentation%20style&width=800&height=600&seq=timber5&orientation=landscape',
    },
    {
      title: 'Ongoing Monitoring & Compliance',
      description: 'Annual or bi-annual surveys for conservation, carbon, and management reporting.',
      why: 'Recurring documentation protects long-term asset value.',
      image: 'https://readdy.ai/api/search-image?query=repeat%20aerial%20views%20of%20managed%20forest%20showing%20seasonal%20changes%20and%20growth%20patterns%20over%20time%20with%20consistent%20documentation%20angle%20and%20clear%20monitoring%20reference%20points%20professional%20forestry%20management%20style&width=800&height=600&seq=timber6&orientation=landscape',
    },
  ];

  const scrollToCalculator = () => {
    document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <SEO
        title="Timber & Forestry Drone Services | Inventory, Health, and Valuation"
        description="Drone services for timber inventory, forest health, LiDAR surveys, and land valuation. Reduce surprises, improve harvest timing, and document assets."
        keywords="Forestry drone services, Timber inventory drone survey, Forest health drone mapping, LiDAR forestry survey, Timber valuation drone data"
      />

      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section */}
        <section className="relative h-[600px] flex items-center justify-center">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=professional%20drone%20flying%20over%20dense%20forest%20canopy%20during%20golden%20hour%20with%20rich%20green%20tree%20tops%20extending%20to%20horizon%20and%20warm%20natural%20lighting%20creating%20depth%20and%20scale%20aerial%20forestry%20survey%20documentation%20style&width=1920&height=600&seq=timberHero&orientation=landscape"
              alt="Drone over forest"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
            <h1 className="text-5xl font-bold mb-6">Drone Intelligence for Timber & Forestry Assets</h1>
            <p className="text-xl mb-8 leading-relaxed">
              When forests aren't measured, decisions rely on assumptions. Drone surveys turn acres into defensible data before harvest, sale, or investment.
            </p>
            <div className="flex gap-4 justify-center">
              <button onClick={scrollToCalculator} className="px-8 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition whitespace-nowrap cursor-pointer">
                Estimate Your Price
              </button>
              <a href="/timber-forestry-pricing" className="px-8 py-3 bg-white text-stone-900 rounded-lg font-semibold hover:bg-stone-100 transition whitespace-nowrap cursor-pointer">
                View Full Pricing
              </a>
            </div>
          </div>
        </section>

        {/* Loss-Aversion Intro */}
        <section className="py-20 bg-stone-50">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-stone-900 mb-6 text-center">What Unmeasured Timber Really Costs</h2>
            <div className="text-lg text-stone-700 leading-relaxed space-y-4">
              <p>In forestry, uncertainty is expensive.</p>
              <p>
                Overestimating volume delays harvests.<br />
                Underestimating value leaves money on the table.<br />
                Missed stress signals turn into lost yield.<br />
                Poor documentation weakens negotiations.
              </p>
              <p className="font-semibold text-stone-900">
                Drone surveys replace assumptions with data before decisions become irreversible.
              </p>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-stone-900 mb-12 text-center">Our Forestry Services</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div key={index} className="bg-stone-50 rounded-lg overflow-hidden hover:shadow-lg transition">
                  <div className="w-full h-48">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-stone-900 mb-3">{service.title}</h3>
                    <p className="text-stone-700 mb-4 text-sm leading-relaxed">{service.description}</p>
                    <div className="pt-4 border-t border-stone-200">
                      <p className="text-sm font-semibold text-amber-700">Why this matters:</p>
                      <p className="text-sm text-stone-600 mt-1">{service.why}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Calculator */}
        <section id="calculator" className="py-20 bg-stone-50">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-stone-900 mb-12 text-center">Instant Forestry Project Estimate</h2>
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Inputs */}
              <div className="bg-white rounded-lg p-8 shadow-sm">
                <h3 className="text-xl font-bold text-stone-900 mb-6">Project Details</h3>

                {/* Acreage */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-stone-700 mb-2">Total Acreage</label>
                  <input
                    type="range"
                    min="10"
                    max="10000"
                    step="10"
                    value={inputs.acreage}
                    onChange={(e) => setInputs({ ...inputs, acreage: Number(e.target.value) })}
                    className="w-full mb-2 cursor-pointer"
                  />
                  <input
                    type="number"
                    min="10"
                    max="10000"
                    value={inputs.acreage}
                    onChange={(e) => setInputs({ ...inputs, acreage: Number(e.target.value) })}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg text-sm cursor-pointer"
                  />
                </div>

                {/* Service Type */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-stone-700 mb-2">Service Type</label>
                  <select
                    value={inputs.serviceType}
                    onChange={(e) => setInputs({ ...inputs, serviceType: e.target.value })}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg text-sm cursor-pointer"
                  >
                    {serviceTypes.map((service) => (
                      <option key={service.value} value={service.value}>
                        {service.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Complexity */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-stone-700 mb-2">Terrain & Canopy Complexity</label>
                  <select
                    value={inputs.complexity}
                    onChange={(e) => setInputs({ ...inputs, complexity: e.target.value })}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg text-sm cursor-pointer"
                  >
                    <option value="standard">Standard</option>
                    <option value="complex">Dense canopy / steep terrain (+20%)</option>
                  </select>
                </div>

                {/* Add-ons */}
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={inputs.priorityTurnaround}
                      onChange={(e) => setInputs({ ...inputs, priorityTurnaround: e.target.checked })}
                      className="w-4 h-4 cursor-pointer"
                    />
                    <span className="text-sm text-stone-700">Priority turnaround (72 hours) +15%</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={inputs.executiveSummary}
                      onChange={(e) => setInputs({ ...inputs, executiveSummary: e.target.checked })}
                      className="w-4 h-4 cursor-pointer"
                    />
                    <span className="text-sm text-stone-700">Executive summary report (investor / buyer-ready) +20%</span>
                  </label>
                </div>
              </div>

              {/* Results */}
              <div className="bg-amber-50 rounded-lg p-8 shadow-sm border-2 border-amber-200">
                <h3 className="text-xl font-bold text-stone-900 mb-6">Estimated Price</h3>
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600">Acres surveyed:</span>
                    <span className="font-semibold text-stone-900">{inputs.acreage.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600">Service:</span>
                    <span className="font-semibold text-stone-900">{getServiceData().label}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600">Adjusted per-acre rate:</span>
                    <span className="font-semibold text-stone-900">
                      ${adjustedRate.low.toFixed(2)} – ${adjustedRate.high.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="py-6 border-t border-amber-300">
                  <div className="text-center">
                    <p className="text-sm text-stone-600 mb-2">Estimated Total Price Range</p>
                    <p className="text-4xl font-bold text-amber-700">
                      ${priceRange.low.toLocaleString()} – ${priceRange.high.toLocaleString()}
                    </p>
                  </div>
                </div>

                <p className="text-xs text-stone-600 mb-6 leading-relaxed">
                  Final pricing depends on access, conditions, and deliverable scope.
                </p>

                <div className="space-y-3">
                  <a href="/instant-quote" className="block w-full px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold text-center hover:bg-amber-700 transition whitespace-nowrap cursor-pointer">
                    Get a Firm Quote
                  </a>
                  <a href="/instant-quote" className="block w-full px-6 py-3 bg-white text-stone-900 rounded-lg font-semibold text-center hover:bg-stone-100 transition border border-stone-300 whitespace-nowrap cursor-pointer">
                    Talk to a Forester
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Drones Reduce Risk */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-stone-900 mb-8 text-center">Why Organizations Choose Drones Over Traditional Methods</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-amber-600"></i>
                <div>
                  <h3 className="font-semibold text-stone-900 mb-1">No Access Barriers</h3>
                  <p className="text-sm text-stone-600">Survey difficult terrain without ground crews</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-amber-600"></i>
                <div>
                  <h3 className="font-semibold text-stone-900 mb-1">Faster Coverage</h3>
                  <p className="text-sm text-stone-600">Complete large acreage surveys in days, not weeks</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-amber-600"></i>
                <div>
                  <h3 className="font-semibold text-stone-900 mb-1">Inspection-Grade Documentation</h3>
                  <p className="text-sm text-stone-600">High-resolution imagery and data for analysis</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-amber-600"></i>
                <div>
                  <h3 className="font-semibold text-stone-900 mb-1">Repeatable Records</h3>
                  <p className="text-sm text-stone-600">Consistent data for year-over-year comparison</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-amber-600"></i>
                <div>
                  <h3 className="font-semibold text-stone-900 mb-1">Defensible Evidence</h3>
                  <p className="text-sm text-stone-600">Timestamped documentation for disputes and compliance</p>
                </div>
              </div>
            </div>
            <p className="text-center text-lg font-semibold text-stone-900">
              When inspections are easier, they happen more often. When they happen more often, failures get smaller.
            </p>
          </div>
        </section>

        {/* Retainer vs One-Off Comparison */}
        <RetainerSavingsChart />

        {/* Who This Is For */}
        <section className="py-20 bg-stone-50">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-stone-900 mb-8 text-center">Who This Is For</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { name: 'Private landowners', icon: 'ri-home-4-line' },
                { name: 'Timber buyers', icon: 'ri-shopping-cart-line' },
                { name: 'Institutional land funds', icon: 'ri-building-2-line' },
                { name: 'Forestry consultants', icon: 'ri-user-settings-line' },
                { name: 'Conservation groups', icon: 'ri-leaf-line' },
                { name: 'Insurance and risk managers', icon: 'ri-shield-check-line' },
              ].map((audience, index) => (
                <div key={index} className="bg-white rounded-lg p-6 text-center shadow-sm">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <i className={`${audience.icon} text-3xl text-amber-600`}></i>
                  </div>
                  <p className="font-semibold text-stone-900">{audience.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=wide%20panoramic%20forest%20landscape%20at%20golden%20hour%20with%20warm%20sunlight%20filtering%20through%20tall%20timber%20trees%20creating%20long%20shadows%20and%20rich%20amber%20tones%20across%20healthy%20woodland%20professional%20nature%20photography%20style&width=1920&height=400&seq=timberCtaGolden&orientation=landscape"
              alt="Forest landscape at golden hour"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
            <h2 className="text-4xl font-bold mb-4">Measure the Asset Before You Decide</h2>
            <p className="text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
              Better data leads to better timing, better pricing, and fewer surprises. Drone forestry services turn land into something you can defend.
            </p>
            <div className="flex gap-4 justify-center">
              <a href="/instant-quote" className="px-8 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition whitespace-nowrap cursor-pointer">
                Request a Quote
              </a>
              <button onClick={() => document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' })} className="px-8 py-3 bg-white text-stone-900 rounded-lg font-semibold hover:bg-stone-100 transition whitespace-nowrap cursor-pointer">
                Estimate Another Project
              </button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default TimberForestryPage;
